/**
 * Event Edit Page
 * 
 * Page for editing existing events
 * 
 * Requirements: 2.7
 */

import { useCallback, useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useEventsStore from '../store/eventsStore';
import { restClient, CreateEventDto, UpdateEventDto, EventType, Location } from '../api/rest-client';
import { Card } from '../components/common/Card';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { EventForm } from '../components/features/events/EventForm';
import { EventUpdateOptionsDialog } from '../components/features/events/EventUpdateOptionsDialog';
import {
  EventUpdateOptions,
  canPromptEventUpdateOptions,
  countRsvpResponses,
  describeUpdateNotificationFailure,
  describeUpdateNotificationOutcome,
} from '../utils/eventUpdate';

export function EventEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedEvent, selectEvent, updateEvent, clearSelectedEvent } = useEventsStore();

  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch event and form data on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('Event ID is required');
        setLoading(false);
        return;
      }

      try {
        const [types, locs] = await Promise.all([
          restClient.getEventTypes(),
          restClient.getLocations(),
          selectEvent(id),
        ]);
        setEventTypes(types);
        // Sort locations alphabetically by name
        setLocations(locs.sort((a, b) => a.name.localeCompare(b.name)));
        setLoading(false);
      } catch (err) {
        setError('Failed to load event data');
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      clearSelectedEvent();
    };
  }, [id, selectEvent, clearSelectedEvent]);

  // --- Post-save options dialog (feature: event-edit-rsvp-reset-update-email) --
  const [showOptionsDialog, setShowOptionsDialog] = useState(false);
  const [submittingOptions, setSubmittingOptions] = useState(false);
  const pendingDtoRef = useRef<UpdateEventDto | null>(null);
  // EventForm awaits onSubmit and only then shows its success toast, so the
  // promise it is awaiting has to stay open until the admin has decided. These
  // hold that promise's handlers across the dialog interaction.
  const pendingResolveRef = useRef<(() => void) | null>(null);
  const pendingRejectRef = useRef<((reason?: unknown) => void) | null>(null);

  /**
   * Perform the actual save, and optionally the notification email.
   *
   * Ordering matters:
   *   1. `updateEvent` is AWAITED. The RSVP reset rides inside that single
   *      request, so if it fails nothing else happens.
   *   2. The email is fired WITHOUT awaiting -- a full fan-out takes ~10s and
   *      the agreed behaviour is not to block the admin on it. Its outcome is
   *      still reported rather than discarded.
   *   3. A failed email does NOT roll back a completed update. A partial
   *      outcome here is reportable, not corrupt. (FR-6.4)
   */
  const performUpdate = useCallback(async (
    eventId: string,
    dto: UpdateEventDto,
    options: EventUpdateOptions,
    eventName: string
  ) => {
    await updateEvent(eventId, dto, { resetRsvps: options.resetRsvps });

    if (options.sendEmail) {
      restClient.sendUpdateNotification(eventId)
        .then(result => {
          // NOTE: describeUpdateNotificationOutcome, NOT
          // describeNotificationOutcome. The cancel helper reads the counts
          // nested under `email`; this endpoint returns them flat, so the
          // cancel helper would read undefined and report every failure --
          // including a total failure -- as success.
          const outcome = describeUpdateNotificationOutcome(result);
          if (outcome.message) {
            alert(outcome.message);
          }
        })
        .catch(() => {
          alert(describeUpdateNotificationFailure(eventName));
        });
    }

    navigate(`/events/${eventId}`);
  }, [updateEvent, navigate]);

  const handleSubmit = useCallback((data: CreateEventDto | UpdateEventDto) => {
    if (!id) return Promise.resolve();
    const dto = data as UpdateEventDto;

    // Events that have already started, and cancelled events, take the
    // pre-existing path unchanged -- no dialog, no new behaviour.
    if (!canPromptEventUpdateOptions(selectedEvent, new Date())) {
      return performUpdate(id, dto, { resetRsvps: false, sendEmail: false },
                           selectedEvent?.event_name ?? '');
    }

    pendingDtoRef.current = dto;
    setShowOptionsDialog(true);
    return new Promise<void>((resolve, reject) => {
      pendingResolveRef.current = resolve;
      pendingRejectRef.current = reject;
    });
  }, [id, selectedEvent, performUpdate]);

  const clearPending = useCallback(() => {
    pendingDtoRef.current = null;
    pendingResolveRef.current = null;
    pendingRejectRef.current = null;
  }, []);

  const handleDialogConfirm = useCallback(async (options: EventUpdateOptions) => {
    const dto = pendingDtoRef.current;
    const resolve = pendingResolveRef.current;
    const reject = pendingRejectRef.current;
    if (!id || !dto) return;

    setSubmittingOptions(true);
    try {
      await performUpdate(id, dto, options, selectedEvent?.event_name ?? '');
      setShowOptionsDialog(false);
      clearPending();
      resolve?.();
    } catch (error) {
      // Let EventForm surface the failure exactly as it does today. The dialog
      // is closed so the admin is returned to the form with their values.
      setShowOptionsDialog(false);
      clearPending();
      reject?.(error);
    } finally {
      setSubmittingOptions(false);
    }
  }, [id, selectedEvent, performUpdate, clearPending]);

  const handleDialogCancel = useCallback(() => {
    const reject = pendingRejectRef.current;
    setShowOptionsDialog(false);
    clearPending();
    // Rejecting is what stops EventForm from announcing "Event updated
    // successfully" for a save that never happened. EventForm will surface this
    // message via its error handler; the wording is chosen to be accurate
    // because it is what the admin will actually read.
    //
    // Known cosmetic limitation: it renders as an error-styled toast rather
    // than a neutral one, because EventForm routes any rejection through
    // handleError. Showing an accurate message in the wrong colour is a much
    // smaller problem than showing a false success, which is what resolving
    // here would produce.
    reject?.(new Error('Update cancelled. No changes were saved.'));
  }, [clearPending]);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !selectedEvent) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="text-center py-12">
            <p className="text-red-600">{error || 'Event not found'}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Event</h1>
        <p className="text-gray-600">Update event details</p>
      </div>

      <Card>
        <EventForm
          event={selectedEvent}
          eventTypes={eventTypes}
          locations={locations}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </Card>

      <EventUpdateOptionsDialog
        isOpen={showOptionsDialog}
        eventName={selectedEvent.event_name}
        rsvpResponseCount={countRsvpResponses(selectedEvent)}
        isSubmitting={submittingOptions}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
      />
    </div>
  );
}

export default EventEdit;
